// LiveStreams.js
'use client';
import { connect } from 'react-redux';

import {
  LIVESTREAM_FEED,
  LIVESTREAM_FEED_SORTINGS,
  FEATURE_LIVESTREAM_INSTANT_COMMAND_TOOLTIP,
} from '../RemoteConfigKeys.js';
import LiveStreams from '../page/LiveStreams.jsx';
import withRouter from '../component/WithRouter.jsx';
import getGoalData from '../selector/getGoalData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getListData from '../selector/getListData.js';
import getLiveStreamIds from '../selector/getLiveStreamIds.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import { getSelectedQueryOptionObject } from '../selector/getFeedQueryObject.js';
import getSelectedFeedQueryOptions from '../selector/getSelectedFeedQueryOptions.js';
import { replace } from '../action/navigationAction.js';
import fetchUser from '../action/fetchUser.js';
import leaveLiveStreams from '../action/leaveLiveStreams.js';
import fetchFeeds from '../action/fetchFeeds.js';
import checkShouldDisplayKaraokeTooltip from '../action/checkShouldDisplayKaraokeTooltip.js';
import setActiveStreamId from '../action/setActiveStreamId.js';
import { closeGameSicBo } from '../action/sicBoActions.js';
import preSubscribeAgoraClient from '../action/preSubscribeAgoraClient.js';
import { karaokeType } from '../resource/goalConstants.js';
import { ExclusivePrivateStatus } from '../resource/liveStreamConstants.js';
import { sanitizeFeedQuery } from '../resource/feedUtils.js';

const mapStateToProps = (
  state,
  { match, userId: userIdArg, streamId: streamIdArg }
) => {
  const userId = userIdArg || match?.params?.userId;
  const streamId = streamIdArg || match?.params?.streamId || userId;
  const feedName = getRemoteConfigData(state, LIVESTREAM_FEED);
  const { feedNameWithQuery } = getSelectedQueryOptionObject(
    state,
    feedName,
    getSelectedFeedQueryOptions(state, LIVESTREAM_FEED, undefined),
    getRemoteConfigData(state, LIVESTREAM_FEED_SORTINGS)?.[0]
  );
  const sanitizedFeedNameWithQuery = sanitizeFeedQuery({
    feedName: feedNameWithQuery,
  });
  const liveStreamSelectPath = ['home', 'users', sanitizedFeedNameWithQuery];
  const meId = getMeData(state, 'id');
  const fetchedLivestreamIds = getListData(
    state,
    liveStreamSelectPath,
    'itemIds'
  );
  const nextPage = getListData(state, liveStreamSelectPath, 'nextPage');
  const isStreamIdLiveStreaming = getLiveStreamData(
    state,
    [streamId],
    'isLiveStreaming'
  );
  const exclusivePrivateStatus = getLiveStreamData(
    state,
    [streamId, 'exclusivePrivate'],
    'status'
  );
  const currentGoalId = getLiveStreamData(
    state,
    [streamId, 'exclusivePrivate'],
    'currentId'
  );
  const currentGoalExclusiveTo = getGoalData(
    state,
    [currentGoalId, 'context'],
    'exclusive_to'
  );
  const karaokeIds = getListData(
    state,
    ['liveStream', streamId, 'karaoke', karaokeType.DEFAULT],
    'itemIds'
  );
  const isStreamIdHasKaraoke = karaokeIds?.length > 0;
  const isPrivateStreamUnlocked = getOperationData(
    state,
    ['liveStream', 'paid', streamId],
    'isPrivateStreamUnlocked'
  );
  const preset = getLiveStreamData(state, [streamId], 'preset');
  const streamIds = getLiveStreamIds(state, streamId);
  const activeUserId = streamId;
  return {
    usingGameId: getOperationData(state, ['game'], 'usingGameId'),
    userId,
    streamId,
    username: getUserData(state, activeUserId, 'username'),
    feedNameWithQuery,
    deviceHeight: getOperationData(state, ['device'], 'height'),
    deviceWidth: getOperationData(state, ['device'], 'width'),
    hasFeedFetched: getNetworkingData(
      state,
      [...liveStreamSelectPath, 1],
      'isFetched'
    ),
    nextPage,
    fetchedLivestreamIds,
    shouldUseInstantCommandTooltip: !!getRemoteConfigData(
      state,
      FEATURE_LIVESTREAM_INSTANT_COMMAND_TOOLTIP
    ),
    isGiftPanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isGiftPanelOpen'
    ),
    isChatPanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isChatPanelOpen'
    ),
    isTriggerPrivatePanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isTriggerPrivatePanelOpen'
    ),
    isGamePanelOpen: getOperationData(
      state,
      ['liveStream', 'panel'],
      'isGamePanelOpen'
    ),
    isGameOpen: getOperationData(state, ['liveStream', 'panel'], 'isGameOpen'),
    isChatroomScrolling: getOperationData(
      state,
      ['liveStream', 'chatroom'],
      'isScrolling'
    ),
    isPrivateStreamUnlocked,
    isStreamIdLiveStreaming,
    exclusivePrivateStatus,
    isExclusivePrivateRelatedToMe:
      [
        ExclusivePrivateStatus.CONSIDERING,
        ExclusivePrivateStatus.ACCEPTED,
        ExclusivePrivateStatus.PREPARING,
        ExclusivePrivateStatus.PERFORMING,
      ].includes(exclusivePrivateStatus) && meId === currentGoalExclusiveTo,
    isStreamIdHasKaraoke,
    hasGroupPrivateStarted: !!getLiveStreamData(
      state,
      [streamId, 'groupPrivate'],
      'currentId'
    ),
    preset,
    streamIds,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    replace: data => dispatch(replace(data)),
    leaveLiveStreams: () => dispatch(leaveLiveStreams()),
    fetchFeeds: payload => dispatch(fetchFeeds(payload)),
    checkShouldDisplayKaraokeTooltip: () =>
      dispatch(checkShouldDisplayKaraokeTooltip()),
    setActiveStreamId: ({ activeStreamId }) =>
      dispatch(setActiveStreamId({ activeStreamId })),
    fetchUser: ({ userId, httpProxyHeaders }) =>
      dispatch(fetchUser({ id: userId, httpProxyHeaders })),
    closeGameSicBo: () => dispatch(closeGameSicBo()),
    preSubscribeAgoraClient: ({ streamId }) =>
      dispatch(preSubscribeAgoraClient({ streamId })),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LiveStreams)
);
